import { useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { X, Loader2, MessagesSquare, Flame, ArrowLeft, Search, Type, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "./ui/switch";
import { ConversationResult, convoToText } from "./ConversationResult";
import { useLang } from "../i18n";
import { PrintsPicker, PrintsThumbs } from "./PrintsPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX = 15000;
const SPEAKER_RE = /^(?:\[[^\]]*\]\s*)?(?:\d{1,2}\/\d{1,2}(?:\/\d{2,4})?,?\s*\d{1,2}:\d{2}\s*-\s*)?([^:\n[\]]{1,30}):\s/;

const inputCls = "w-full resize-none bg-white border-[2px] border-black rounded-xl px-3 py-2.5 text-sm font-medium placeholder:text-[#999] focus:outline-none focus:ring-4 focus:ring-[#BD00FF]";

export const ConversationModal = ({ noFilter, onToggleFilter, onClose, onNeedCredits, onDone }) => {
  const { t, lang } = useLang();
  const [step, setStep] = useState("input");
  const [convo, setConvo] = useState("");
  const [context, setContext] = useState("");
  const [question, setQuestion] = useState("");
  const [item, setItem] = useState(null);
  const [source, setSource] = useState("text");
  const [prints, setPrints] = useState([]);
  const busy = useRef(false);

  const stats = useMemo(() => {
    const lines = convo.split("\n").map((l) => l.trim()).filter(Boolean);
    const people = new Set();
    lines.forEach((l) => { const m = l.match(SPEAKER_RE); if (m) people.add(m[1].trim()); });
    return { lines: lines.length, people: [...people].slice(0, 6) };
  }, [convo]);

  const toReview = () => {
    if (source === "images") {
      if (!prints.length) { toast.error(t.pr_empty); return; }
      setStep("review");
      return;
    }
    if (convo.trim().length < 20) { toast.error(t.cv_too_short); return; }
    if (convo.length > MAX) { toast.error(t.cv_too_long); return; }
    setStep("review");
  };

  const submit = async () => {
    if (busy.current) return;
    busy.current = true;
    setStep("loading");
    try {
      const common = { context: context || null, question: question || null, no_filter: noFilter, lang };
      const { data } = source === "images"
        ? await axios.post(`${API}/analyze-conversation-images`, { ...common, images: prints.map((p) => ({ data: p.src })) })
        : await axios.post(`${API}/analyze-conversation`, { ...common, conversation: convo.trim() });
      setItem(data);
      setStep("result");
      onDone();
    } catch (e) {
      setStep("review");
      const s = e.response?.status;
      if (s === 402) onNeedCredits();
      else if (s === 413) toast.error(source === "images" ? t.pr_too_big : t.cv_too_long);
      else if (s === 415) toast.error(t.pr_bad_format);
      else if (s === 422) toast.error(t.pr_unreadable);
      else if (!e.response) toast.error(t.cv_network);
      else toast.error(t.cv_fail);
    } finally {
      busy.current = false;
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(convoToText(item.result, t));
    toast.success(t.copied);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" data-testid="conversation-modal">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl bg-[#F4F0E6] border-[4px] border-black rounded-2xl shadow-[8px_8px_0px_0px_#000] my-8">
        <div className="flex items-center justify-between px-5 py-4 border-b-[3px] border-black">
          <h2 className="font-display font-bold text-xl uppercase tracking-tight flex items-center gap-2">
            <MessagesSquare size={20} strokeWidth={2.5} /> {t.cv_btn}
          </h2>
          <button data-testid="conversation-close-btn" onClick={onClose} disabled={step === "loading"} className="border-[2px] border-black rounded-full p-1 bg-white hover:bg-[#FFD500] transition-colors disabled:opacity-50">
            <X size={18} strokeWidth={2.5} />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-4">
          {step === "input" && (
            <>
              <div className="grid grid-cols-2 gap-3" data-testid="conversation-source-selector">
                {[["text", Type, t.pr_tab_text], ["images", ImageIcon, t.pr_tab_images]].map(([id, Icon, label]) => (
                  <button key={id} data-testid={`conversation-source-${id}`} onClick={() => setSource(id)}
                    className={`flex items-center justify-center gap-2 border-[3px] border-black rounded-xl px-2 py-2.5 font-bold uppercase text-xs sm:text-sm shadow-[3px_3px_0px_0px_#000] transition-colors ${source === id ? "bg-[#FFD500]" : "bg-white"}`}>
                    <Icon size={16} strokeWidth={2.5} /> {label}
                  </button>
                ))}
              </div>
              {source === "images" ? <PrintsPicker prints={prints} setPrints={setPrints} /> : (<>
              <p className="text-sm font-medium text-[#555]">{t.cv_sub}</p>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#444] mb-2">{t.cv_convo_label}</p>
                <textarea data-testid="conversation-input" value={convo} onChange={(e) => setConvo(e.target.value)}
                  placeholder={t.cv_convo_ph} rows={9}
                  className="w-full resize-y bg-white border-[3px] border-black rounded-xl p-3 text-sm font-medium placeholder:text-[#999] focus:outline-none focus:ring-4 focus:ring-[#BD00FF]" />
                <p data-testid="conversation-char-count" className={`mt-1 text-xs font-bold text-right ${convo.length > MAX ? "text-[#FF3366]" : "text-[#888]"}`}>
                  {convo.length.toLocaleString()} / {MAX.toLocaleString()}
                </p>
                {convo.length > MAX && <p data-testid="conversation-too-long" className="text-xs font-bold text-[#FF3366]">{t.cv_too_long}</p>}
              </div>
              </>)}
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#444] mb-2">{t.cv_context_label}</p>
                <textarea data-testid="conversation-context-input" value={context} onChange={(e) => setContext(e.target.value)}
                  placeholder={t.cv_context_ph} rows={2} maxLength={1000} className={inputCls} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#444] mb-2">{t.cv_question_label}</p>
                <input data-testid="conversation-question-input" value={question} onChange={(e) => setQuestion(e.target.value)}
                  placeholder={t.cv_question_ph} maxLength={300} className={inputCls} />
              </div>
              <div className={`flex items-center gap-3 border-[3px] border-black rounded-xl px-4 py-3 ${noFilter ? "bg-[#FF3366]" : "bg-white"}`}>
                <Flame size={20} strokeWidth={2.5} className={noFilter ? "text-white" : "text-black"} />
                <span className={`font-bold uppercase tracking-[0.06em] text-sm flex-1 ${noFilter ? "text-white" : "text-black"}`}>{t.no_filter}</span>
                <Switch data-testid="conversation-no-filter-toggle" checked={noFilter} onCheckedChange={onToggleFilter}
                  className="data-[state=checked]:bg-black data-[state=unchecked]:bg-white border-[2px] border-black" />
              </div>
              <button data-testid="conversation-review-btn" onClick={toReview}
                className="w-full flex items-center justify-center gap-2 bg-[#FFD500] border-[3px] border-black rounded-xl py-3 font-display font-bold uppercase tracking-tight text-lg shadow-[4px_4px_0px_0px_#000] transition-[transform,box-shadow] duration-100 hover:shadow-[2px_2px_0px_0px_#000] hover:translate-x-[2px] hover:translate-y-[2px]">
                <Search size={18} strokeWidth={2.5} /> {t.cv_review_btn}
              </button>
            </>
          )}

          {(step === "review" || step === "loading") && (
            <>
              <div data-testid="conversation-review" className="border-[3px] border-black rounded-xl bg-white p-4 shadow-[4px_4px_0px_0px_#000] space-y-2">
                <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#BD00FF]">{t.cv_review_title}</p>
                {source === "images" ? (<>
                  <p className="text-xs font-bold text-[#444]">{prints.length} {t.pr_prints_word} · {noFilter ? t.no_filter_ribbon : t.cv_with_filter}</p>
                  <PrintsThumbs prints={prints} />
                </>) : (<>
                <p className="text-xs font-bold text-[#444]">
                  {stats.lines} {t.cv_lines}{stats.people.length > 0 && ` · ${t.cv_people}: ${stats.people.join(", ")}`} · {noFilter ? t.no_filter_ribbon : t.cv_with_filter}
                </p>
                <pre className="whitespace-pre-wrap font-sans text-sm text-[#222] bg-[#F4F0E6] border-[2px] border-black rounded-lg p-3 max-h-60 overflow-y-auto">{convo.trim()}</pre>
                </>)}
                {context && <p className="text-sm"><b>{t.cv_context_label}:</b> {context}</p>}
                {question && <p className="text-sm"><b>{t.cv_question_label}:</b> {question}</p>}
              </div>
              <p className="text-xs font-semibold text-[#666] text-center">{t.cv_cost}</p>
              <div className="flex gap-3">
                <button data-testid="conversation-edit-btn" onClick={() => setStep("input")} disabled={step === "loading"}
                  className="flex items-center justify-center gap-1.5 bg-white border-[3px] border-black rounded-xl px-4 py-3 font-bold uppercase text-sm shadow-[4px_4px_0px_0px_#000] disabled:opacity-50">
                  <ArrowLeft size={16} strokeWidth={2.5} /> {t.cv_edit}
                </button>
                <button data-testid="conversation-analyze-btn" onClick={submit} disabled={step === "loading"}
                  className="flex-1 flex items-center justify-center gap-2 bg-[#00FFA3] border-[3px] border-black rounded-xl py-3 font-display font-bold uppercase tracking-tight text-lg shadow-[4px_4px_0px_0px_#000] transition-[transform,box-shadow] duration-100 hover:shadow-[2px_2px_0px_0px_#000] hover:translate-x-[2px] hover:translate-y-[2px] disabled:opacity-70">
                  {step === "loading" ? <><Loader2 className="animate-spin" size={18} /> {source === "images" ? t.pr_loading : t.cv_loading}</> : <><MessagesSquare size={18} strokeWidth={2.5} /> {t.cv_confirm}</>}
                </button>
              </div>
            </>
          )}

          {step === "result" && item && (
            <>
              <ConversationResult result={item.result} noFilter={item.no_filter} onCopy={copy} />
              <button data-testid="conversation-new-btn" onClick={() => { setItem(null); setConvo(""); setContext(""); setQuestion(""); setPrints([]); setStep("input"); }}
                className="w-full flex items-center justify-center gap-2 bg-[#FFD500] border-[3px] border-black rounded-xl py-3 font-bold uppercase text-sm tracking-wide shadow-[4px_4px_0px_0px_#000] transition-[transform,box-shadow] duration-100 hover:shadow-[2px_2px_0px_0px_#000] hover:translate-x-[2px] hover:translate-y-[2px]">
                <MessagesSquare size={16} strokeWidth={2.5} /> {t.cv_new}
              </button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};
